import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator } from 'lucide-react';
import { useAccountingOverviewQuery } from '@/client/api/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { filterClass } from './components/spend-ui';
import { InOutChart } from './components/report-ui';
import { EmptyState, Panel } from '../HumanResource/components/hr-ui';
import { money, totals } from '../HumanResource/components/comp-format';

const monthName = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });

function Todo({ to, label, value, tone = 'normal' }: { to: string; label: string; value: string; tone?: 'normal' | 'warn' }) {
  return (
    <Link to={to} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2.5 text-sm hover:bg-gray-50">
      <span className="text-gray-700">{label}</span>
      <span className={`flex items-center gap-1.5 font-medium ${tone === 'warn' ? 'text-amber-700' : 'text-gray-900'}`}>
        {value} <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
      </span>
    </Link>
  );
}

/** The accounting front page: this month and the year so far, and what is waiting on someone. */
export default function OverviewPage() {
  const q = useAccountingOverviewQuery();
  const data = q.data;
  const currencies = useMemo(
    () => [...new Set((data?.pnl.rows ?? []).filter((r) => r.totals.income || r.totals.costs).map((r) => r.currency))].sort(),
    [data],
  );
  const [picked, setPicked] = useState<string | null>(null);
  const currency = picked && currencies.includes(picked) ? picked : currencies[0] ?? 'GHS';

  const ytd = data?.pnl.totals.find((t) => t.currency === currency);
  const cashYtd = data?.cash.totals.find((t) => t.currency === currency);
  const month = data?.pnl.rows.find((r) => r.month === data.month && r.currency === currency);
  const points = (data?.pnl.months ?? []).map((m) => {
    const r = data?.pnl.rows.find((x) => x.month === m && x.currency === currency);
    return { month: m, a: r?.totals.income ?? 0, b: r?.totals.costs ?? 0, net: r?.net ?? 0 };
  });

  return (
    <AccountingPageShell
      mock={false}
      title="Accounting"
      description="Earned, spent and owed, from revenue, bills, expense claims and payroll. Per currency; nothing is converted."
      crumbs={[{ label: 'Accounting' }]}
      icon={Calculator}
      actions={
        currencies.length > 1 ? (
          <select value={currency} onChange={(e) => setPicked(e.target.value)} className={filterClass} aria-label="Currency">
            {currencies.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        ) : undefined
      }
      stats={[
        { key: 'm', label: data ? `Profit, ${monthName(data.month)}` : 'Profit this month', value: month ? money(month.net, currency) : '—' },
        { key: 'income', label: 'Income, year to date', value: ytd ? money(ytd.income, currency) : '—' },
        { key: 'costs', label: 'Costs, year to date', value: ytd ? money(ytd.costs, currency) : '—' },
        { key: 'cash', label: 'Net cash, year to date', value: cashYtd ? money(cashYtd.net, currency) : '—' },
      ]}
    >
      {q.isError ? (
        <EmptyState label={`Could not load the overview: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading || !data ? (
        <EmptyState label="Adding it all up…" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <Panel
            title={`Income and costs, ${data.pnl.months[0]?.slice(0, 4) ?? ''}`}
            className="lg:col-span-2"
            action={
              <Link to="/admin/accounting/reports/profit-loss" className="text-xs font-medium text-blue-700 hover:underline">
                Profit & loss
              </Link>
            }
          >
            <InOutChart points={points} currency={currency} labels={['Income', 'Costs']} />
          </Panel>
          <Panel title="Waiting on someone">
            <div className="space-y-2">
              <Todo to="/admin/accounting/payables/bills" label="Bills owed" value={totals(data.owed)} />
              <Todo
                to="/admin/accounting/payables/bills"
                label="Overdue"
                value={totals(data.overdue)}
                tone={data.overdue.length ? 'warn' : 'normal'}
              />
              <Todo to="/admin/accounting/expenses" label="Expense claims open" value={totals(data.expensesOpen)} />
              {data.payRunsOpen.length === 0 ? (
                <Todo to="/admin/accounting/payroll/runs" label="Pay runs" value="none open" />
              ) : (
                data.payRunsOpen.map((r) => (
                  <Todo
                    key={r.id}
                    to={`/admin/accounting/payroll/runs?run=${r.id}`}
                    label={`${monthName(r.period)} payroll`}
                    value={r.status === 'draft' ? 'to approve' : 'to pay'}
                    tone="warn"
                  />
                ))
              )}
              <Todo to="/admin/accounting/receivables/revenue" label="Revenue detail" value="open" />
              <Todo to="/admin/accounting/reports/cash-flow" label="Cash flow" value="open" />
            </div>
          </Panel>
        </div>
      )}
    </AccountingPageShell>
  );
}
